/**
 * Bridge between the UI and the `@tbc/core` coaching engine.
 *
 * The engine evaluates 150+ biomechanics-derived rules against a typed
 * SwingMetrics + session aggregates and returns the hits sorted by severity.
 * This adapter converts those hits into the string-list shape the UI expects.
 */
import type { SwingMetrics } from '@tbc/core';
import { coaching } from '@tbc/core';

export interface CoachingFeedback {
  readonly tips: readonly string[];
}

const MAX_TIPS_PER_SWING = 5;

function buildAggregates(all: readonly SwingMetrics[]): coaching.Aggregates {
  if (all.length === 0) {
    return { sessionSwingCount: 0, medianPeakWristAngVel: 0, medianElbowAtContactDeg: 0 };
  }
  const sorted = (vals: number[]) => [...vals].sort((a, b) => a - b);
  const median = (vals: number[]): number => {
    if (vals.length === 0) return 0;
    const s = sorted(vals);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 === 0 ? ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2 : (s[mid] ?? 0);
  };
  return {
    sessionSwingCount: all.length,
    medianPeakWristAngVel: median(all.map((s) => s.peakWristAngularVelocityRadPerSec)),
    medianElbowAtContactDeg: median(
      all.map((s) => s.elbowAngleAtContactDeg).filter((v) => Number.isFinite(v)),
    ),
  };
}

/**
 * Feedback for a specific swing within a session. Returns the top-N
 * warning/tip hits (warnings first) as localized strings for display.
 */
export function getCoachingFeedback(
  swing: SwingMetrics | undefined,
  _index: number,
  sessionSwings: readonly SwingMetrics[] = swing ? [swing] : [],
): CoachingFeedback {
  if (!swing) return { tips: [] };
  const aggregates = buildAggregates(sessionSwings);
  const hits = coaching.evaluate(swing, aggregates);
  const tips = hits.slice(0, MAX_TIPS_PER_SWING).map((h) => h.rule.feedbackPt);
  return { tips };
}
